/**
 * Matches a 3- or 6-digit hex color with an optional leading `#`. Anchored, so
 * it rejects anything with surrounding characters or whitespace. Has no `g`
 * flag, so the instance is safe to share across `.test`/`.exec` calls.
 */
export const HEX_COLOR_REGEX = /^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

/** Whether `color` is a clean 3- or 6-digit hex literal (with optional `#`). */
export function isValidHexColor(color: string): boolean {
  return HEX_COLOR_REGEX.test(color);
}

export interface RgbaColor {
  r: number;
  g: number;
  b: number;
  a: number;
}

/** Parse a 3- or 6-digit hex; undefined for anything else so callers skip
 *  rather than guess. */
export function parseHexColor(value: string): RgbaColor | undefined {
  const match = HEX_COLOR_REGEX.exec(value.trim());
  const captured = match?.[1];
  if (!captured) {
    return undefined;
  }
  const full =
    captured.length === 3
      ? captured
          .split("")
          .map((channel) => channel + channel)
          .join("")
      : captured;
  return {
    r: parseInt(full.slice(0, 2), 16),
    g: parseInt(full.slice(2, 4), 16),
    b: parseInt(full.slice(4, 6), 16),
    a: 1,
  };
}

export function colorToHex({ r, g, b }: RgbaColor): string {
  return `#${[r, g, b]
    .map((channel) => Math.round(channel).toString(16).padStart(2, "0"))
    .join("")}`.toUpperCase();
}

/** Source-over composite of `foreground` onto `background`. */
export function compositeColors(
  foreground: RgbaColor,
  background: RgbaColor,
): RgbaColor {
  const alpha = foreground.a + background.a * (1 - foreground.a);
  if (alpha === 0) {
    return { r: 0, g: 0, b: 0, a: 0 };
  }

  const channel = (fg: number, bg: number) =>
    Math.round(
      (fg * foreground.a + bg * background.a * (1 - foreground.a)) / alpha,
    );

  return {
    r: channel(foreground.r, background.r),
    g: channel(foreground.g, background.g),
    b: channel(foreground.b, background.b),
    a: alpha,
  };
}
